import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAccess } from '../../Hooks/useAdminAccess';
import { sponsorshipService } from '../../Services/sponsorshipService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  Users, 
  FileText, 
  TrendingUp, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  RefreshCw,
  BarChart3,
  Package
} from 'lucide-react';

/**
 * Main Admin Dashboard with Overview API
 * Shows comprehensive metrics from the dashboard overview endpoint
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { hasAdminAccess, isAdminActive } = useAdminAccess();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dashboard overview data
  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    if (!isAdminActive()) return;

    setLoading(true);
    setError(null);
    try {
      const data = await sponsorshipService.dashboard.getOverview();
      setDashboardData(data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if user doesn't have admin access
  useEffect(() => {
    if (!hasAdminAccess()) {
      navigate('/dashboard');
    }
  }, [hasAdminAccess, navigate]);

  if (!hasAdminAccess()) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive mb-4">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Error loading dashboard</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadDashboardData} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">No dashboard data available</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { current_week, contracts, inventory, recent_activity, generated_at } = dashboardData;

  // Determine fill rate status
  const getFillRateStatus = (fillRate) => {
    if (fillRate >= 80) return { variant: 'default', color: 'text-green-600', bg: 'bg-green-50' };
    if (fillRate >= 60) return { variant: 'secondary', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { variant: 'destructive', color: 'text-red-600', bg: 'bg-red-50' };
  };

  const fillRateStatus = getFillRateStatus(current_week.fill_rate);
  const utilizationStatus = getFillRateStatus(inventory.utilization_percent);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Guided Plan Sponsorship System
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            Last updated: {new Date(generated_at).toLocaleString()}
          </div>
          <Button onClick={loadDashboardData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Current Week Performance */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Current Week Performance</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Week</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {current_week.year}-W{String(current_week.week).padStart(2, '0')}
              </div>
              <p className="text-xs text-muted-foreground">
                Current assignment period
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Assigned Users</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-900">{current_week.assigned_users}</div>
              <p className="text-xs text-green-700">
                of {current_week.total_assignments} total
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unfilled Users</CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-900">{current_week.unfilled_users}</div>
              <p className="text-xs text-orange-700">
                Waiting for sponsors
              </p>
            </CardContent>
          </Card>

          <Card className={`border-${fillRateStatus.color.split('-')[1]}-200 ${fillRateStatus.bg}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Fill Rate</CardTitle>
              <TrendingUp className={`h-4 w-4 ${fillRateStatus.color}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${fillRateStatus.color.replace('text-', 'text-').replace('-600', '-900')}`}>
                {current_week.fill_rate.toFixed(1)}%
              </div>
              <p className={`text-xs ${fillRateStatus.color.replace('-600', '-700')}`}>
                {current_week.assigned_users}/{current_week.total_assignments} assigned
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Contract Health */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Contract Health</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/contracts')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{contracts.active_contracts}</div>
              <p className="text-xs text-muted-foreground">
                Currently running
              </p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/sponsors')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Sponsors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{contracts.total_sponsors}</div>
              <p className="text-xs text-muted-foreground">
                Unique sponsors
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weeks Purchased</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{contracts.total_weeks_purchased}</div>
              <p className="text-xs text-muted-foreground">
                Total commitment
              </p>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Weeks Delivered</CardTitle>
              <CheckCircle className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-900">{contracts.total_weeks_delivered}</div>
              <p className="text-xs text-blue-700">
                of {contracts.total_weeks_purchased} purchased
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Inventory Status */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Inventory Status</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/inventory')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Eligible Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inventory.eligible_users}</div>
              <p className="text-xs text-muted-foreground">
                Available for assignment
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Capacity</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inventory.total_capacity_weeks}</div>
              <p className="text-xs text-muted-foreground">
                Weeks available
              </p>
            </CardContent>
          </Card>

          <Card className={`border-${utilizationStatus.color.split('-')[1]}-200 ${utilizationStatus.bg}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Utilization</CardTitle>
              <TrendingUp className={`h-4 w-4 ${utilizationStatus.color}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${utilizationStatus.color.replace('text-', 'text-').replace('-600', '-900')}`}>
                {inventory.utilization_percent.toFixed(1)}%
              </div>
              <p className={`text-xs ${utilizationStatus.color.replace('-600', '-700')}`}>
                {inventory.allocated_weeks}/{inventory.total_capacity_weeks} weeks used
              </p>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Remaining Capacity</CardTitle>
              <Package className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-900">{inventory.remaining_weeks}</div>
              <p className="text-xs text-purple-700">
                Weeks available
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Recent Activity (Last 7 Days)</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Assignments</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{recent_activity.assignments_last_7_days}</div>
              <p className="text-xs text-muted-foreground">
                Last 7 days
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Recent Fill Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{recent_activity.recent_fill_rate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground">
                Last 7 days performance
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common administrative tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Button onClick={() => navigate('/admin/sponsors')} variant="outline" className="w-full">
              <Users className="h-4 w-4 mr-2" />
              Manage Sponsors
            </Button>
            <Button onClick={() => navigate('/admin/contracts')} variant="outline" className="w-full">
              <FileText className="h-4 w-4 mr-2" />
              View Contracts
            </Button>
            <Button onClick={() => navigate('/admin/inventory')} variant="outline" className="w-full">
              <BarChart3 className="h-4 w-4 mr-2" />
              Check Inventory
            </Button>
            <Button onClick={() => navigate('/admin/reports')} variant="outline" className="w-full">
              <TrendingUp className="h-4 w-4 mr-2" />
              Generate Reports
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
